import React, { useMemo } from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { getAircraftSpritePosition, getAircraftSizeMultiplier } from '../utils/aircraftSpriteMap';
import { getAirlineLogo, hasAirlineLogo, getMilitaryLogo, hasMilitaryLogo, useAirlineLogoMapVersion } from '../utils/airlineLogoMap';
import { isMilitaryAircraft, aircraftCountryCode } from '../utils/icaoCountryMap';
import { isGroundVehicle as checkGroundVehicle } from '../utils/groundVehicle';
import { getEmergencySquawk } from '../utils/emergencySquawk';
import { getColourSettings } from '../utils/mapStyles';
import { getAircraftBitmap, getShadowBitmap, useSpriteSheetReady } from '../utils/markerBitmaps';
import './AircraftMarker.css';
import '../assets/flags.css';
import { airlineCodeFromCallsign } from '../utils/callsignAirline';
import { GROUND_VEHICLE_SVG_PATHS } from './AircraftTableComponents';

// Zoom thresholds for mobile-friendly display
const LABEL_HIDE_ZOOM_THRESHOLD = 7; // Hide labels below this zoom level (lowered from 9)
const ZOOM_ICON_SCALE_MIN = 5; // Minimum zoom for scaling calculation
const ZOOM_ICON_SCALE_MAX = 12; // Maximum zoom for scaling calculation

// Degrees of heading change needed before the icon is rebuilt. See iconHeading
// below for why rebuilding the icon is expensive enough to be worth quantising.
const ICON_HEADING_STEP_DEG = 3;

// Quantisation for the SHADOW, which is derived from altitude.
//
// Quantising heading (above) fixed only half the icon-churn problem. The icon useMemo also
// depends on shadowOffset and shadowScale, and both are continuous functions of alt_baro:
//   shadowOffset = min(3 + alt * 0.001, 10)         -> moves below 7,000 ft
//   shadowScale  = max(1 - alt * 0.00000375, 0.85)  -> moves below 40,000 ft
// ADS-B altitude arrives in 25 ft steps and jitters constantly even in level cruise, and the
// React.memo comparator re-renders on every alt_baro change — so for climbing, descending, and
// even cruising aircraft the icon was still rebuilt roughly once per second per aircraft, with
// the same consequence as before: setIcon() -> _initIcon() REPLACES the marker's DOM element
// (and, before the bitmap markers, reallocated both heavily filtered layers).
//
// One 25 ft step moves the shadow by 0.025px and the scale by 0.003px on a 32px sprite — invisible,
// but enough to bust a float dependency. At 500 ft bands the worst-case visible step is 0.5px of
// offset and 0.06px of scale, still imperceptible, while a cruising aircraft crosses a band
// essentially never and a 2000 ft/min climb crosses one every 15s instead of every update.
const SHADOW_ALT_STEP_FT = 500;

function AircraftMarker({ aircraft, isSelected = false, onAircraftSelect, zoomLevel = 8, autoZoom = 'none', selectedReceiver = 'All Receivers', colourSettings, ghostMode = false }) {
  useAirlineLogoMapVersion();
  // Markers are pre-rendered bitmaps (see utils/markerBitmaps.js); nothing to
  // draw until the sprite sheet has decoded, ~100 ms after first load.
  const spriteSheetReady = useSpriteSheetReady();
  const position = [aircraft.lat, aircraft.lon];
  const label = aircraft.flight ? aircraft.flight.trim() : aircraft.hex;

  // Get colour settings (use prop if provided, otherwise load from localStorage)
  const colours = colourSettings || getColourSettings();

  // Determine if labels should be shown based on zoom level
  // Always show labels when a specific receiver is selected AND auto-zoom is active
  const isAutoZoomWithReceiver = autoZoom !== 'none' && selectedReceiver !== 'All Receivers';
  const showLabel = isAutoZoomWithReceiver || zoomLevel >= LABEL_HIDE_ZOOM_THRESHOLD;

  // Check if this is a military aircraft - prefer backend flag, fallback to category check
  const isMilitary = aircraft.military === true || isMilitaryAircraft(aircraft.category);

  // Determine logo path based on military status
  let logoPath = null;
  let hasLogo = false;

  if (isMilitary) {
    // For military aircraft, use country code to get military insignia
    // Pass operator name to try to get service-specific icon (e.g., Navy, Air Force)
    const countryCode = aircraftCountryCode(aircraft);
    const operatorName = aircraft.airline_name || aircraft.operator;
    if (countryCode && hasMilitaryLogo(countryCode)) {
      logoPath = getMilitaryLogo(countryCode, operatorName);
      hasLogo = true;
    }
  } else {
    // For civilian aircraft, use airline logo
    // Try to get airline code from airline_icao field first
    let airlineCode = aircraft.airline_icao;

    // If not available, try the callsign — but not when the callsign is the
    // registration (M-ASBA -> "MASBA" is not Malaysia Airlines)
    if (!airlineCode && aircraft.flight) {
      const extractedCode = airlineCodeFromCallsign(aircraft.flight, { registration: aircraft.r });
      if (extractedCode && hasAirlineLogo(extractedCode)) {
        airlineCode = extractedCode;
      }
    }

    if (airlineCode && hasAirlineLogo(airlineCode)) {
      logoPath = getAirlineLogo(airlineCode, 'map'); // dark variant if the airline has one
      hasLogo = true;
    }
  }

  // Get heading from true_heading, mag_heading, or track (in order of preference)
  const heading = aircraft.true_heading || aircraft.mag_heading || aircraft.track || 0;

  // Quantised heading for the ICON ONLY. The icon is an HTML string built in a
  // useMemo below; any change to it makes Leaflet's setIcon() -> _initIcon()
  // REPLACE the marker's DOM element rather than patch it. Raw heading moves on
  // almost every position update, so the icon was being torn down and rebuilt
  // roughly once per second per aircraft. (The layers used to carry CSS filters
  // — colour chain + 4x drop-shadow, and brightness/opacity/blur — which made
  // that churn crash the tab in Firefox and, on a flaky GPU driver, in Chrome;
  // they are pre-rendered bitmaps now, see utils/markerBitmaps.js. Rebuilding
  // is still a DOM replacement, so the quantisation stays.)
  //
  // Rounding to 3 degrees cuts icon rebuilds by roughly an order of magnitude.
  // On a ~24px sprite a 3 degree step is not perceptible, and position updates
  // still move the marker every time — only the ROTATION is quantised.
  const iconHeading = Math.round(heading / ICON_HEADING_STEP_DEG) * ICON_HEADING_STEP_DEG;

  // Get the sprite position and size multiplier for this aircraft type
  const spritePos = getAircraftSpritePosition(aircraft.t);
  const sizeMultiplier = getAircraftSizeMultiplier(aircraft.t);

  // Calculate zoom-based scale factor (smaller icons when zoomed out)
  // At zoom <= 5: scale is 0.5, at zoom >= 12: scale is 1.0
  const clampedZoom = Math.max(ZOOM_ICON_SCALE_MIN, Math.min(zoomLevel, ZOOM_ICON_SCALE_MAX));
  const zoomScale = 0.5 + ((clampedZoom - ZOOM_ICON_SCALE_MIN) / (ZOOM_ICON_SCALE_MAX - ZOOM_ICON_SCALE_MIN)) * 0.5;

  const iconSize = Math.round(32 * sizeMultiplier * zoomScale);

  // Calculate tooltip offset based on icon size (closer for smaller icons)
  const tooltipOffset = -Math.round(iconSize * 0.3);

  // Calculate shadow distance and size based on altitude.
  // Base shadow at 3px, add 0.001px per foot of altitude (max 10px total offset).
  //
  // Quantised to SHADOW_ALT_STEP_FT bands before use — see the constant for why. Number() rather
  // than `|| 0` because alt_baro is the string "ground" for surface targets, which is truthy and
  // would propagate NaN into the transform (`top: NaNpx`, `scale(NaN)`).
  const rawAltitude = Number(aircraft.alt_baro);
  const altitude = Number.isFinite(rawAltitude) ? rawAltitude : 0;
  const shadowAltitude = Math.round(altitude / SHADOW_ALT_STEP_FT) * SHADOW_ALT_STEP_FT;
  const baseShadow = 3;
  const shadowOffset = Math.min(baseShadow + (shadowAltitude * 0.001), 10);

  // Scale shadow down with altitude (higher = smaller shadow)
  // Scale from 1.0 at ground level to 0.85 at 40,000 ft
  const shadowScale = Math.max(1.0 - (shadowAltitude * 0.00000375), 0.85);

  // Determine if this is a new aircraft (never seen before).
  // Prefer the explicit backend flag — Map.js's parked-aircraft path
  // builds marker objects without last_seen_at, so the old heuristic
  // (!last_seen_at) marked every parked aircraft as new and painted
  // them green. Fall back to the heuristic only when the backend
  // hasn't supplied a flag at all.
  const isNewAircraft = (typeof aircraft.isNew === 'boolean')
    ? aircraft.isNew
    : !aircraft.last_seen_at;

  // Marker colour and outline, resolved to a pre-rendered bitmap instead of a
  // CSS filter chain (see utils/markerBitmaps.js for why). Precedence:
  // selected > new > military > normal. A first-ever sighting is the rarer,
  // more interesting fact, so green wins over military grey — the military
  // insignia on the label still says which of the green ones are military.
  // Ghost mode (Departures map context traffic) forces grey and the wrapper
  // applies opacity 0.5 so they read as background.
  // Squawk 7500/7600/7700. This is the one fact that outranks everything else on the
  // map: an aircraft in distress must not be reading as "selected orange" or "new
  // green" because it also happens to be either. Only ghost mode (Departures context
  // traffic, deliberately backgrounded) stays ahead of it.
  const emergency = getEmergencySquawk(aircraft);
  // Kept as PRIMITIVES for the icon useMemo below. getEmergencySquawk() builds a fresh
  // object every render, so depending on it directly would bust the memo on every
  // update — and a busted memo means setIcon() -> _initIcon() REPLACES the marker's
  // DOM node, which is the exact churn the heading/shadow quantisation above exists to
  // prevent. A string code and a string colour compare by value and stay stable.
  const emergencySquawk = emergency ? emergency.code : null;
  const emergencyRingColour = colours.aircraftEmergency || '#E02020';

  const iconColour = ghostMode
    ? (colours.aircraftMilitary || '#C0C0C0')
    : emergency
    ? (colours.aircraftEmergency || '#E02020')
    : isSelected
    ? colours.aircraftSelected
    : isNewAircraft
    ? colours.aircraftNew
    : isMilitary
    ? (colours.aircraftMilitary || '#C0C0C0')
    : colours.aircraftNormal;
  const outlineColour = (isSelected && !ghostMode) ? '#000' : '#333';

  const aircraftBitmap = spriteSheetReady
    ? getAircraftBitmap({ col: spritePos.col, row: spritePos.row, size: iconSize, colour: iconColour, outline: outlineColour })
    : null;
  const shadowBitmap = spriteSheetReady
    ? getShadowBitmap({ col: spritePos.col, row: spritePos.row, size: iconSize })
    : null;

  // Check if this is a ground vehicle (backend flag OR callsign pattern)
  const isGroundVehicle = checkGroundVehicle(aircraft);

  // No operator mark (GA, business jets, airlines without a logo): the label
  // carries the aircraft's country flag in the logo's place instead.
  const flagCountry = !hasLogo && !isGroundVehicle ? aircraftCountryCode(aircraft) : null;

  // Create a unique icon for each aircraft based on heading, type, altitude, and size
  const icon = useMemo(() => {
    const halfSize = iconSize / 2;

    // Ground vehicles get a truck icon instead of aircraft sprite
    if (isGroundVehicle) {
      const vSize = Math.max(18, Math.round(iconSize * 0.7));
      const vHalf = Math.round(vSize / 2);
      return L.divIcon({
        html: `<div style="position: relative; width: ${vSize}px; height: ${vSize}px;">
                 <svg class="ground-vehicle-icon" width="${vSize}" height="${vSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(1px 1px 2px rgba(0,0,0,0.5));">${GROUND_VEHICLE_SVG_PATHS}</svg>
               </div>`,
        iconSize: [vSize, vSize],
        iconAnchor: [vHalf, vHalf],
        className: 'aircraft-marker ground-vehicle-marker'
      });
    }

    if (!aircraftBitmap || !shadowBitmap) {
      return null; // sprite sheet not decoded yet
    }
    // Plain <img> layers: no CSS filter anywhere on the marker. The rotate /
    // translate transforms below are composited and cost nothing per frame.
    const layer = (src) => `<img src="${src}" width="${iconSize}" height="${iconSize}" draggable="false" alt="" style="display: block; width: ${iconSize}px; height: ${iconSize}px; pointer-events: none;">`;

    // Anomaly indicator ring (pulsing border for GO_AROUND or HOLDING)
    const anomalyState = aircraft.anomaly_state;
    const anomalyRing = anomalyState ? `<div style="position: absolute; top: -4px; left: -4px; width: ${iconSize + 8}px; height: ${iconSize + 8}px; border: 2px solid ${anomalyState === 'GO_AROUND' ? '#f97316' : '#ef4444'}; border-radius: 50%; animation: anomaly-pulse 1.5s ease-in-out infinite; pointer-events: none;"></div>` : '';

    // Emergency ring — the same mechanism, drawn wider and heavier so it still reads
    // when an anomaly ring is also present. This exists so the emergency does NOT
    // depend on hue: in the mono theme, and for a red-green colour-blind viewer, the
    // pulsing ring is what carries the signal. Replaces the anomaly ring rather than
    // stacking with it, since a distress call outranks a go-around.
    const emergencyRing = emergencySquawk
      ? `<div style="position: absolute; top: -6px; left: -6px; width: ${iconSize + 12}px; height: ${iconSize + 12}px; border: 3px solid ${emergencyRingColour}; border-radius: 50%; animation: anomaly-pulse 1s ease-in-out infinite; pointer-events: none;"></div>`
      : '';

    // Ghost-mode wrapper drops opacity to 50% so the icon reads as
    // contextual background rather than primary content.
    const wrapperStyle = ghostMode
      ? `position: relative; width: ${iconSize}px; height: ${iconSize}px; opacity: 0.5;`
      : `position: relative; width: ${iconSize}px; height: ${iconSize}px;`;
    return L.divIcon({
      html: `<div style="${wrapperStyle}">
               ${emergencyRing || anomalyRing}
               <div style="position: absolute; top: ${shadowOffset}px; left: ${shadowOffset}px; transform: rotate(${iconHeading}deg) scale(${shadowScale}); transform-origin: center; width: ${iconSize}px; height: ${iconSize}px;">
                 ${layer(shadowBitmap)}
               </div>
               <div style="position: absolute; top: 0; left: 0; transform: rotate(${iconHeading}deg); transform-origin: center; width: ${iconSize}px; height: ${iconSize}px;">
                 ${layer(aircraftBitmap)}
               </div>
             </div>`,
      iconSize: [iconSize, iconSize],
      iconAnchor: [halfSize, halfSize],
      className: ghostMode ? 'aircraft-marker aircraft-marker-ghost' : 'aircraft-marker'
    });
  }, [iconHeading, shadowOffset, shadowScale, aircraftBitmap, shadowBitmap, iconSize, isGroundVehicle, aircraft.anomaly_state, ghostMode, emergencySquawk, emergencyRingColour]);

  if (aircraft.lat == null || aircraft.lon == null || !icon) {
    return null;
  }

  const handleClick = () => {
    if (onAircraftSelect) {
      onAircraftSelect(aircraft);
    }
  };

  return (
    <Marker
      position={position}
      icon={icon}
      eventHandlers={{
        click: handleClick
      }}
    >
      {showLabel && (
        <Tooltip permanent direction="top" offset={[0, tooltipOffset]} className="aircraft-tooltip">
          <div className="tooltip-content">
            {/* Predicted landing (or, on Departures, departure) order, set only by the
                Predictions pages — the same orange number as the table's # column.
                _seqTitle names which order it is; arrivals leave it unset. */}
            {aircraft._seqNumber != null && (
              <span className="tooltip-seq" title={aircraft._seqTitle || 'Predicted landing order'}>{aircraft._seqNumber}</span>
            )}
            {hasLogo && logoPath && (
              <img
                src={logoPath}
                alt={isMilitary ? 'Military' : (aircraft.airline_icao || '')}
                className="tooltip-airline-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            {flagCountry && (
              <span className={`flag flag-${flagCountry} tooltip-flag`} aria-label={flagCountry.toUpperCase()} />
            )}
            <span className="tooltip-label">{label}</span>
          </div>
        </Tooltip>
      )}
    </Marker>
  );
}

// Memoize component with custom comparison to prevent re-renders when aircraft hasn't changed
export default React.memo(AircraftMarker, (prevProps, nextProps) => {
  const prev = prevProps.aircraft;
  const next = nextProps.aircraft;

  // Only re-render if these key properties changed
  return (
    prev.hex === next.hex &&
    prev.lat === next.lat &&
    prev.lon === next.lon &&
    prev.flight === next.flight &&
    prev._seqNumber === next._seqNumber &&
    prev._seqTitle === next._seqTitle &&
    prev.t === next.t &&
    prev.true_heading === next.true_heading &&
    prev.mag_heading === next.mag_heading &&
    prev.track === next.track &&
    prev.alt_baro === next.alt_baro &&
    prev.airline_icao === next.airline_icao &&
    // PRESENCE, not value. last_seen_at ticks on every single message, so an
    // equality check here returned false for every aircraft on every update and
    // this memo never prevented a single re-render — which is what kept feeding
    // the icon rebuild described above. Nothing rendered depends on its VALUE:
    // its only use is the isNewAircraft fallback (!aircraft.last_seen_at), and
    // that is a presence test. isNew itself is compared separately below.
    (!prev.last_seen_at) === (!next.last_seen_at) &&
    prev.isNew === next.isNew &&
    prev.military === next.military &&
    prev.category === next.category &&
    prev.dbFlags_country === next.dbFlags_country &&
    prev.country_code === next.country_code &&
    prev.country === next.country &&
    prev.airline_name === next.airline_name &&
    prev.operator === next.operator &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.ghostMode === nextProps.ghostMode &&
    prevProps.zoomLevel === nextProps.zoomLevel &&
    prevProps.autoZoom === nextProps.autoZoom &&
    prevProps.selectedReceiver === nextProps.selectedReceiver &&
    // Compare actual colour values that affect rendering
    prevProps.colourSettings?.aircraftNormal === nextProps.colourSettings?.aircraftNormal &&
    prevProps.colourSettings?.aircraftNew === nextProps.colourSettings?.aircraftNew &&
    prevProps.colourSettings?.aircraftSelected === nextProps.colourSettings?.aircraftSelected &&
    prevProps.colourSettings?.aircraftMilitary === nextProps.colourSettings?.aircraftMilitary
  );
});